/**
 * Utility functions for consistent staff and client name/label formatting
 * across all pages (Roster, Calendar, Shift Management, etc.)
 */

import { DropdownOption } from '../services/rosterService';

/**
 * Interface for entities that can have names/labels
 */
export interface NamedEntity {
  id: number;
  name?: string;
  label?: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  first_name?: string;
  last_name?: string;
  position?: string;
  dateOfBirth?: string;
}

/**
 * Format date of birth for display
 */
const formatDateOfBirth = (dateOfBirth: string): string => {
  if (!dateOfBirth) return '';
  
  try {
    // Handle different date formats (ISO string, date-only, etc.)
    const date = new Date(dateOfBirth);
    if (isNaN(date.getTime())) {
      // If it's not a valid date, return the original string
      return dateOfBirth.split('T')[0]; // Extract date part if it looks like ISO
    }
    
    // Format as YYYY-MM-DD
    return date.toISOString().split('T')[0];
  } catch (error) {
    // Fallback to extracting date part
    return dateOfBirth.split('T')[0];
  }
};

/**
 * Type guard to check if entity has firstName/lastName properties
 */
const hasNameProperties = (entity: any): entity is NamedEntity => {
  return entity && (
    'firstName' in entity || 
    'lastName' in entity || 
    'first_name' in entity || 
    'last_name' in entity ||
    'fullName' in entity ||
    'position' in entity ||
    'dateOfBirth' in entity
  );
};

/**
 * Get consistent staff display name/label
 * Format: FirstName LastName (DOB: YYYY-MM-DD) - Position
 */
export const getStaffDisplayName = (
  staff: NamedEntity | DropdownOption | null | undefined, 
  fallback?: string
): string => {
  if (!staff) {
    return fallback || 'Unknown Staff';
  }

  // Check if entity has extended name properties
  if (hasNameProperties(staff)) {
    // Construct from firstName + lastName
    const firstName = staff.firstName || staff.first_name || '';
    const lastName = staff.lastName || staff.last_name || '';
    
    if (firstName || lastName) {
      let displayName = `${firstName} ${lastName}`.trim();
      
      // Add date of birth if available
      if (staff.dateOfBirth) {
        const dob = formatDateOfBirth(staff.dateOfBirth);
        displayName += ` (DOB: ${dob})`;
      }
      
      // Add position if available for additional context
    //   if (staff.position) {
    //     displayName += ` - ${staff.position}`;
    //   }
      
      return displayName;
    }

    // Fallback to fullName if firstName/lastName not available
    if (staff.fullName) {
      let displayName = staff.fullName;
      
      // Add date of birth if available
      if (staff.dateOfBirth) {
        const dob = formatDateOfBirth(staff.dateOfBirth);
        displayName += ` (DOB: ${dob})`;
      }
      
      // Add position if available
      if (staff.position) {
        displayName += ` - ${staff.position}`;
      }
      
      return displayName;
    }
  }

  // Third priority: use label if available (from dropdown)
  if (staff.label) {
    return staff.label;
  }

  // Fourth priority: use name field
  if (staff.name) {
    return staff.name;
  }

  // Fallback with ID
  return fallback || `Staff ${staff.id}`;
};

/**
 * Get consistent client display name/label
 * Format: FirstName LastName (DOB: YYYY-MM-DD)
 */
export const getClientDisplayName = (
  client: NamedEntity | DropdownOption | null | undefined, 
  fallback?: string
): string => {
  if (!client) {
    return fallback || 'Unknown Client';
  }

  // Check if entity has extended name properties
  if (hasNameProperties(client)) {
    // Construct from firstName + lastName
    const firstName = client.firstName || client.first_name || '';
    const lastName = client.lastName || client.last_name || '';
    
    if (firstName || lastName) {
      let displayName = `${firstName} ${lastName}`.trim();
      
      // Add date of birth if available
      if (client.dateOfBirth) {
        const dob = formatDateOfBirth(client.dateOfBirth);
        displayName += ` (DOB: ${dob})`;
      }
      
      return displayName;
    }

    // Fallback to fullName if firstName/lastName not available
    if (client.fullName) {
      let displayName = client.fullName;
      
      // Add date of birth if available
      if (client.dateOfBirth) {
        const dob = formatDateOfBirth(client.dateOfBirth);
        displayName += ` (DOB: ${dob})`;
      }
      
      return displayName;
    }
  }

  // Third priority: use label if available (from dropdown)
  if (client.label) {
    return client.label;
  }

  // Fourth priority: use name field
  if (client.name) {
    return client.name;
  }

  // Fallback with ID
  return fallback || `Client ${client.id}`;
};

/**
 * Find staff by ID from a list and return display name
 */
export const getStaffNameById = (
  staffId: number, 
  staffList: (NamedEntity | DropdownOption)[], 
  fallback?: string
): string => {
  const staff = staffList.find(s => s.id === staffId);
  return getStaffDisplayName(staff, fallback);
};

/**
 * Find client by ID from a list and return display name
 */
export const getClientNameById = (
  clientId: number, 
  clientList: (NamedEntity | DropdownOption)[], 
  fallback?: string
): string => {
  const client = clientList.find(c => c.id === clientId);
  return getClientDisplayName(client, fallback);
};

/**
 * Enrich shifts with consistent staff and client names
 */
export const enrichShiftsWithNames = <T extends { staffId: number; clientId: number; staffName?: string; clientName?: string }>(
  shifts: T[],
  staffList: (NamedEntity | DropdownOption)[],
  clientList: (NamedEntity | DropdownOption)[]
): (T & { staffName: string; clientName: string })[] => {
  return shifts.map(shift => ({
    ...shift,
    staffName: shift.staffName || getStaffNameById(shift.staffId, staffList, `Staff ${shift.staffId}`),
    clientName: shift.clientName || getClientNameById(shift.clientId, clientList, `Client ${shift.clientId}`)
  }));
};

/**
 * Helper to get display name for dropdown options specifically
 */
export const getDropdownDisplayName = (option: DropdownOption): string => {
  return option.label || option.name || `ID ${option.id}`;
};
